import { lazy, Suspense } from 'react';
import PictureImage from '../Common/PictureImage';
import { FBA_PREP_HERO_ALT, FBA_PREP_HERO_SRC } from '../../seo/imageAltText';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './amazonFBAprep.css';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import RelatedLinks from '../Common/RelatedLinks';
import PageLastUpdated from '../Common/PageLastUpdated';
import Fbabanner from './Fbabanner';
import FBAPrepServices from './FbaPrepServices';
import FbaPricingTrustBlock from './FbaPricingTrustBlock';
import { FBA_PREP_CONTACT_PATH, FBA_PREP_PRIMARY_CTA } from './fbaPrepCta';
import LoadingFallback from '../Common/LoadingFallback';
import Canonical from '../../seo/Canonical';
import FAQSection from '../FAQSection';
import { AMAZON_FBA_PREP_SERVICE_FAQS } from '../../data/amazonFbaPrepServiceFaqs';
import {
  COMPANY_FBA_PREP_FROM,
  COMPANY_PICKING_ACCURACY,
} from '../../seo/companyFacts';
import '../Homepage/FAQ.css';

// Lazy load pricing components
const FBAPricing = lazy(() => import('./Fbapricing'));
const FBAPricingTiers = lazy(() => import('./FBAPricingTiers'));

const AmazonFBAPrep = () => {
  // Stats data for mobile slider
  const stats = [
    {
      number: '50,000+',
      numberClass: 'amazon-stat-number-orange',
      title: 'Units Prepped / Month',
      description: "Your inventory moves faster with reliable ecommerce fulfilment UK."
    },
    {
      number: COMPANY_PICKING_ACCURACY,
      numberClass: 'amazon-stat-number-blue',
      title: 'Picking / prep accuracy target',
      description: 'Barcode-verified prep so Amazon inbound and pick accuracy stay high.'
    },
    {
      number: '24-48h',
      numberClass: 'amazon-stat-number-orange',
      title: 'Turnaround',
      description: 'From startup launches to scale, our Amazon prep centre UK grows with you.'
    },
    {
      number: `From ${COMPANY_FBA_PREP_FROM}`,
      numberClass: 'amazon-stat-number-blue',
      title: 'Transparent FBA prep pricing',
      description: 'Published Amazon FBA prep pricing from 30p per unit with no hidden charges.'
    }
  ];

  // FAQ rendering + JSON-LD is handled by `FAQSection` below.

  return (
    <div className="amazon-fba-page">
      <Helmet>
        <meta name="keywords" content="amazon prep centre uk, amazon fba prep centre uk, fba prep service uk, prep center amazon uk, uk amazon prep center, amazon fba prep center uk, fba prep service, amazon fba prep centre, amazon fba prep, amazon fba prep service, amazon preparation service, Amazon 3PL UK" />
        <meta httpEquiv="X-Robots-Tag" content="index, follow" />
        <meta name="publisher" content="Pick Pack Pro" />
      </Helmet>
      <Canonical href="https://www.pickpackpro.co.uk/amazon-fba-prep-services" />
      {/* Hero Header Section */}
   <section className="amazon-fba-hero">

      {/* Full-bleed background image — dark overlay applied via ::after in CSS */}
      <div className="amazon-fba-hero-background">
        <PictureImage
          src={FBA_PREP_HERO_SRC}
          alt={FBA_PREP_HERO_ALT}
          title="Amazon FBA prep centre UK warehouse — Pick Pack Pro"
          className="amazon-fba-hero-image"
          width={1600}
          height={900}
          decoding="async"
          isHero
        />
      </div>

      {/* Content */}
      <div className="amazon-fba-hero-container">
        <div className="amazon-fba-hero-content">

          {/* Left: Text */}
          <div className="amazon-fba-hero-left">

            {/* Heading */}
            <h1 className="amazon-fba-hero-title">
              Amazon FBA Prep Service UK
            </h1>

            <PageLastUpdated path="/amazon-fba-prep-services" className="page-last-updated--on-dark page-last-updated--hero" />

            <p className="amazon-fba-hero-description">
              <strong>What is Amazon FBA prep?</strong> Amazon FBA prep in the UK is the process of inspecting, labelling, protecting and packing your inventory so Amazon fulfilment centres accept it without delays or chargebacks. Pick Pack Pro is a UK-based 3PL and Amazon FBA prep centre operating from Milton Keynes, supporting Amazon, Shopify, TikTok Shop and multi-channel sellers. We handle your entire inbound workflow — inspection,{' '}
              <Link to="/blog/fnsku-labelling-explained-amazon-barcode-rules-for-2026">FNSKU labelling</Link>,{' '}
              <Link to="/blog/amazon-fba-polybagging-suffocation-warning-rules-2026-guide">polybagging</Link> (including applying <strong>suffocation warning labels</strong> to any <strong>amazon poly bag</strong>), bundling, carton prep and forwarding — typically in 24–48 hours, from {COMPANY_FBA_PREP_FROM}. Our comprehensive <strong>amazon preparation service</strong> ensures 100% compliance with all inbound guidelines.
            </p>
            <p className="amazon-fba-hero-description">
              See{' '}
              <Link to="/amazon-fba-prep-pricing">Amazon FBA prep cost &amp; pricing</Link>, tour our{' '}
              <Link to="/amazon-fba-prep-centre-services">FBA prep centre facility</Link>, or explore{' '}
              <Link to="/amazon-fba-prep-private-label">private label FBA prep UK</Link> and{' '}
              <Link to="/amazon-fba-prep-wholesale">wholesale FBA prep UK</Link>. New to inbound prep? Read{' '}
              <Link to="/blog/what-is-fba-prep">what is FBA prep</Link> or{' '}
              <Link to="/blog/how-to-prep-products-for-amazon-fba-uk">step-by-step FBA prep instructions</Link>.
            </p>

            {/* 2-column feature grid */}
            <div className="amazon-fba-hero-features">
              {[
                `Amazon FBA Prep from Just ${COMPANY_FBA_PREP_FROM}`,
                'Amazon-Compliant Inventory Preparation',
                'Typical 24–48 Hour Turnaround',
                'Dedicated Fulfilment Account Manager',

              ].map((item) => (
                <div className="amazon-fba-feature-item" key={item}>
                  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M4 10.5l4 4 8-8"
                      stroke="#ffffff"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="amazon-fba-hero-buttons">
              <Link to={FBA_PREP_CONTACT_PATH} className="amazon-fba-cta-button-primary">
                {FBA_PREP_PRIMARY_CTA}
              </Link>
              <a href="#fba-pricing-trust" className="amazon-fba-cta-button-secondary">
                View pricing from 30p per unit
              </a>
            </div>
          </div>

          {/* Right: Lead Capture Form */}
          <div className="amazon-fba-hero-right"></div>
        </div>
      </div>
    </section>

      <FbaPricingTrustBlock />

      <section className="amazon-stats-section" aria-labelledby="fba-prep-how-title">
        <div className="amazon-stats-container">
          <div className="amazon-fba-how-grid">
            <div>
              <h2 id="fba-prep-how-title" className="amazon-stats-title" style={{ textAlign: "left", marginBottom: "16px" }}>
            <span style={{ color: '#28479e' }}>How does our UK Amazon FBA prep centre </span>
            <span style={{ color: '#f78c45' }}>process your stock?</span>
          </h2>
          <p style={{ margin: '0 0 24px', fontSize: '16px', lineHeight: '1.6', color: '#4a5568' }}>
            We guarantee 100% compliance with <a href="https://sellercentral.amazon.co.uk/help/hub/reference/G200141500" target="_blank" rel="noopener noreferrer nofollow" style={{ color: '#28479e', textDecoration: 'underline' }}>Amazon's official packaging guidelines</a> before inventory leaves our 3PL logistics hub.
          </p>
          <ol style={{ margin: '0 0 28px', paddingLeft: '1.25rem', color: '#4a5568', lineHeight: 1.6, textAlign: 'left' }}>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>Receive &amp; Inspect:</strong> We verify counts and SKU condition against your ASN.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>Prep &amp; Protect:</strong> FNSKU labels, polybags, and bundling applied strictly to Amazon rules.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>Dispatch:</strong> Carton labels applied and SPD/LTL routed to Amazon FCs.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>Sync:</strong> Real-time tracking updates your Seller Central account.
            </li>
          </ol>
            </div>
                    <aside className="fba-pricing-trust-testimonial" aria-label="Client testimonial" style={{ margin: 0, height: 'fit-content' }}>
          <div className="fba-pricing-trust-stars" aria-hidden="true">
            ★★★★★
          </div>
          <blockquote cite="/our-reviews">
            &ldquo;Switching to Pick Pack Pro cut our UK prep costs by 18%. Their 30p base rate is
            completely transparent, and items hit LTN1 within 24 hours.&rdquo;
          </blockquote>
          <cite>— Mark T., 7-Figure Amazon FBA Seller</cite>
          <Link to="/our-reviews" className="fba-pricing-trust-reviews-link">
            Read More Verified Client Success Stories →
          </Link>
        </aside>
          </div>
          <p style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 40px', fontSize: '16px', lineHeight: '1.6', color: '#4a5568' }}>
            Accuracy target: {COMPANY_PICKING_ACCURACY}. Prep from {COMPANY_FBA_PREP_FROM}. See the{' '}
            <Link to="/amazon-fba-prep-centre-services">facility tour</Link>,{' '}
            <Link to="/amazon-fba-prep-pricing">pricing</Link>, or our{' '}
            <Link to="/shopify-fulfillment-services">Shopify 3PL integrations</Link>.
          </p>
          
          {/* Desktop Grid */}
          <div className="amazon-stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="amazon-stat-card">
                <div className={`amazon-stat-number ${stat.numberClass}`}>{stat.number}</div>
                <h3 className="amazon-stat-title">{stat.title}</h3>
                <p className="amazon-stat-description">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <FBAPrepServices />
      <Suspense fallback={<LoadingFallback />}>
        <FBAPricing primaryCtaLabel={FBA_PREP_PRIMARY_CTA} />
      </Suspense>
      <Suspense fallback={<LoadingFallback />}>
        <FBAPricingTiers primaryCtaLabel={FBA_PREP_PRIMARY_CTA} />
      </Suspense>

      {/* Content Section */}
      {/* <section className="amazon-fba-content-section">
        <div className="amazon-fba-content-container">
          <div className="amazon-fba-content-grid">
            <div className="amazon-fba-content-card">
              <div className="amazon-fba-card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                  <line x1="7" y1="7" x2="7.01" y2="7"/>
                </svg>
              </div>
              <h3 className="amazon-fba-card-title">Product Inspection</h3>
              <p className="amazon-fba-card-description">
                Thorough inspection of all products to ensure they meet Amazon's quality 
                standards before processing.
              </p>
            </div>

            <div className="amazon-fba-content-card">
              <div className="amazon-fba-card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                  <line x1="12" y1="22.08" x2="12" y2="12"/>
                </svg>
              </div>
              <h3 className="amazon-fba-card-title">FNSKU Labeling</h3>
              <p className="amazon-fba-card-description">
                Professional application of FNSKU labels according to Amazon's specifications 
                for proper product identification.
              </p>
            </div>

            <div className="amazon-fba-content-card">
              <div className="amazon-fba-card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <line x1="9" y1="3" x2="9" y2="21"/>
                </svg>
              </div>
              <h3 className="amazon-fba-card-title">Poly Bagging</h3>
              <p className="amazon-fba-card-description">
                Secure poly bagging with suffocation warnings and proper sizing to protect 
                your products during transit.
              </p>
            </div>

            <div className="amazon-fba-content-card">
              <div className="amazon-fba-card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="8" width="18" height="4" rx="1"/>
                  <path d="M12 8v13"/>
                  <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>
                  <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/>
                </svg>
              </div>
              <h3 className="amazon-fba-card-title">Bubble Wrapping</h3>
              <p className="amazon-fba-card-description">
                Protective bubble wrapping for fragile items to ensure they arrive at Amazon 
                fulfillment centers in perfect condition.
              </p>
            </div>

            <div className="amazon-fba-content-card">
              <div className="amazon-fba-card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                </svg>
              </div>
              <h3 className="amazon-fba-card-title">Case Packing</h3>
              <p className="amazon-fba-card-description">
                Proper case packing with correct quantities and labeling to meet Amazon's 
                case pack requirements.
              </p>
            </div>

            <div className="amazon-fba-content-card">
              <div className="amazon-fba-card-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <h3 className="amazon-fba-card-title">Shipment Plans</h3>
              <p className="amazon-fba-card-description">
                Creation and management of Amazon shipment plans to ensure smooth delivery 
                to fulfillment centers.
              </p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Find Amazon 3PL Section */}
      {/* <WhyChoose /> */}
      <Fbabanner showPrimaryCta />

      {/* AEO Definition Section */}
      <section 
        className="amazon-fba-definition-section"
        style={{ 
          position: 'relative',
          padding: '5rem 1.5rem',
          backgroundImage: 'url(/Images/amazon-fba-prep-warehouse-milton-keynes.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#e2e8f0',
          lineHeight: '1.8',
          fontSize: '1.1rem'
        }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.85)' }}></div>
        <div style={{ position: 'relative', maxWidth: '900px', margin: '0 auto', zIndex: 1 }}>
          <p style={{ marginBottom: '1.5rem' }}>
            <strong style={{ color: '#ffffff', fontSize: '1.15rem' }}>What is Amazon FBA prep?</strong> Amazon FBA prep in the UK is the process of inspecting, labelling, protecting and packing your inventory so Amazon fulfilment centres accept it without delays or chargebacks. Pick Pack Pro is a UK-based 3PL and Amazon FBA prep centre operating from Milton Keynes, supporting Amazon, Shopify, TikTok Shop and multi-channel sellers. We handle your entire inbound workflow — inspection, <Link to="/blog/fnsku-labelling-explained-amazon-barcode-rules-for-2026" style={{ color: '#818cf8', textDecoration: 'none' }}>FNSKU labelling</Link>, <Link to="/blog/amazon-fba-polybagging-suffocation-warning-rules-2026-guide" style={{ color: '#818cf8', textDecoration: 'none' }}>polybagging</Link> (including applying <strong style={{ color: '#ffffff' }}>suffocation warning labels</strong> to any <strong style={{ color: '#ffffff' }}>amazon poly bag</strong>), bundling, carton prep and forwarding — typically in 24–48 hours, from 30p/unit. Our comprehensive <strong style={{ color: '#ffffff' }}>amazon preparation service</strong> ensures 100% compliance with all inbound guidelines.
          </p>
          <p>
            See <Link to="/amazon-fba-prep-pricing" style={{ color: '#818cf8', textDecoration: 'none' }}>Amazon FBA prep cost &amp; pricing</Link>, tour our <Link to="/amazon-fba-prep-centre-services" style={{ color: '#818cf8', textDecoration: 'none' }}>FBA prep centre facility</Link>, or explore <Link to="/amazon-fba-prep-private-label" style={{ color: '#818cf8', textDecoration: 'none' }}>private label FBA prep UK</Link> and <Link to="/amazon-fba-prep-wholesale" style={{ color: '#818cf8', textDecoration: 'none' }}>wholesale FBA prep UK</Link>. New to inbound prep? Read <Link to="/blog/what-is-fba-prep" style={{ color: '#818cf8', textDecoration: 'none' }}>what is FBA prep</Link> or <Link to="/blog/how-to-prep-products-for-amazon-fba-uk" style={{ color: '#818cf8', textDecoration: 'none' }}>step-by-step FBA prep instructions</Link>.
          </p>
        </div>
      </section>

      <section style={{ maxWidth: '900px', margin: '4rem auto 0', padding: '0 1.5rem' }}>
        <div style={{ background: '#f8fafc', borderLeft: '4px solid #f58030', border: '1px solid #e2e8f0', padding: '1.5rem', borderRadius: '8px', fontSize: '1rem', lineHeight: '1.6', color: '#1a202c' }}>
          <strong style={{ display: 'block', marginBottom: '0.75rem', color: '#2d3748', fontSize: '1.15rem' }}>Executive Summary</strong>
          <p style={{ margin: '0 0 1rem 0' }}><strong>Who this is for & Use Cases:</strong> Pick Pack Pro provides a specialist Amazon FBA Prep Service designed for UK-based Private Label sellers, Wholesale importers, and multi-channel brands who need guaranteed compliance with Amazon's inbound requirements.</p>
          <p style={{ margin: 0 }}><strong>Our Original Experience & Real Data:</strong> Based on our first-hand experience processing over 500,000 FBA units, we guarantee 100% compliance with all FNSKU, polybagging, and box-weight rules. According to official <a href="https://sellercentral.amazon.co.uk/help/hub/reference/G200141500" target="_blank" rel="noopener noreferrer nofollow" style={{ color: '#f58030', textDecoration: 'underline' }}>Amazon Seller Central documentation</a>, non-compliant inventory can lead to refusal or chargebacks — our rigorous prep process protects your metrics and supply chain.</p>
        </div>
      </section>

      <FAQSection
        headingId="fba-faq"
        className="faqs-section"
        title="Frequently Asked Questions"
        description="Find clear answers about turnaround times, Amazon compliance, integrations, pricing, and everything included in our Milton Keynes FBA prep service."
        faqs={AMAZON_FBA_PREP_SERVICE_FAQS}
      />
      {/* <Ctasection /> */}

      {/* <WhyPPP /> */}
      {/* <FBAFaq /> */}
      <RelatedLinks
        title="FBA prep guides"
        serviceLinks={[
          { to: '/blog/what-is-fba-prep', label: 'What is FBA prep? (complete guide)' },
          { to: '/blog/fba-prep-requirements-uk-amazon-seller-guide-2026', label: 'FBA prep requirements UK (2026 guide)' },
          { to: '/blog/amazon-fba-prep-service-uk', label: 'Amazon FBA prep service UK — what to look for' },
          { to: '/blog/amazon-fba-prep-service-fees-explained', label: 'Amazon FBA prep service fees explained' },
          { to: '/blog/fnsku-labelling-service-uk', label: 'FNSKU labelling service UK' },
        ]}
        blogLink={{ to: '/fulfillment-blog', label: 'All fulfilment blog articles' }}
      />
      <Newsletter />
      <Footer />
    </div>
  );
};

export default AmazonFBAPrep;
